import { defaultFillColor, defaultZoneFillColor, zoneColors } from './displayModeHelper';

/* eslint-disable no-bitwise */

/**
 * If zoneColors has mapping for zoneName, then display color based on mapping
 * Else convert zoneName to a hex color value by hashing
 */
export const stringToColor = (zoneName: string, opacity = 1): string => {
  // if zoneColors has mapping for zoneName, then return matched color
  if (zoneColors[zoneName]) {
    return zoneColors[zoneName];
  }

  let hash = 0;

  // Define rgb color array
  const color = [0, 0, 0];

  for (let i = 0; i < zoneName.length; ) {
    hash = zoneName.charCodeAt(i) + ((hash << 5) - hash);
    i += 1;
  }

  for (let i = 0; i < 3; ) {
    const value = (hash >> (i * 8)) & 0xff;
    color[i] = value;
    i += 1;
  }

  // Format the result as rgba
  return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${opacity})`;
};

const GOLDEN_ANGLE = 137.508;
const HSL_SATURATION = 55;
const HSL_LIGHTNESS = 62;

/**
 * Converts HSL values to a 6-digit hex string (e.g. "#a3c45f").
 * Required because floorplan-core's createWeightedGradient only accepts hex.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const color = lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Returns a hex color for the given index using golden-angle spacing.
 * Pure function — same index always returns the same color.
 */
export function subcategoryColorByIndex(index: number): string {
  const hue = (index * GOLDEN_ANGLE) % 360;
  return hslToHex(hue, HSL_SATURATION, HSL_LIGHTNESS);
}

/**
 * Number of color buckets for stable per-key coloring. 64 is a balance between
 * visual distinctness (golden-angle spacing keeps adjacent buckets distinguishable)
 * and collision rate (with ~10 Drop Zones, the chance of a same-bucket collision
 * is small enough not to matter in practice).
 */
const COLOR_BUCKETS = 64;

/**
 * Returns a deterministic golden-angle color for a stable key (e.g. a Drop Zone's
 * categoryId). The same key always maps to the same color, regardless of list
 * ordering — so a Drop Zone looks identical in the create view and the read view.
 *
 * Tradeoff: determinism over uniqueness. Two different keys can collide on the
 * same bucket; the practical Drop Zone count keeps that vanishingly unlikely.
 */
export function colorByKey(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + key.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }
  const bucket = Math.abs(hash) % COLOR_BUCKETS;
  return subcategoryColorByIndex(bucket);
}

/**
 * return color based on zoneName and fixtureId
 * if zoneColors has mapping for zoneName, then display color based on mapping
 * else if it's agile fixture and zoneName is not empty, generate color ramdonly base on zoneName
 * else if it's agile, display defaultZoneFillColor
 * for non-agile fixtures, display defaultFillColor
 */
const getColorForText = (zoneName: string, isAgile: boolean): string => {
  if (isAgile) {
    if (zoneName) {
      return stringToColor(zoneName);
    }
    return defaultZoneFillColor;
  }
  return defaultFillColor;
};

export default getColorForText;
